import { updateConsents } from "@flow/common/consent_logic";
import { NO_EMAIL_PROVIDED_IDENT } from "@flow/common/constants";
import { calculateActionsTarget } from "@flow/common/logic/action_helpers";
import { mergeContexts } from "@flow/common/logic/journey";
import { Consent, ConsentConfig, JourneyContext, PublicCampaignTarget } from "@flow/common/types";
import { ConsentInputComponentBlock } from "@flow/frontend/common/ConsentInputComponentBlock";
import { captchaExecute, loadCaptcha } from "@flow/frontend/member/captcha";
import { ActionComponentWrapper } from "@flow/frontend/member/components/ActionComponentWrapper";
import { CampaignTargetLookup } from "@flow/frontend/member/components/CampaignTargetLookup";
import { EmailInput } from "@flow/frontend/member/components/EmailInput";
import { PostcodeInput } from "@flow/frontend/member/components/PostcodeInput";
import { RequiredTextArea } from "@flow/frontend/member/components/RequiredTextArea";
import { memberTheme } from "@flow/frontend/member/memberTheme";
import { partitionConsentConfigs } from "@flow/frontend/member/member_utils";
import { ActionComponentProps } from "@flow/frontend/member/plugin_api";
import { useUserAnalyticsService } from "@flow/frontend/member/user_analytics_service";
import {
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React from "react";
import { ActionCustomData } from "../../common/action_types";
import { signPetition } from "../../common/routes";
import { RecentSignatures } from "./RecentSignatures";
import { MemberLoadingSpinner } from "@flow/frontend/member/components/MemberLoadingSpinner";

// SW-200 - Temporary EmailIsOptIn identifier control.
export const EMAIL_IS_OPT_IN_CONSENT_METHOD = "onfieldpresent";
// SW-200 - Return true if there is a SINGLE EmailIsOptIn consent configuration. False otherwise. Enables the feature.
export const hasSingleEmailIsConsentConfiguration = (props: ActionComponentProps<ActionCustomData>): boolean => {
  const emailIsConsentConfigs = findEmailIsConsentConfigurations(props);
  if (emailIsConsentConfigs.length > 1) {
    console.log(
      "ERROR: More than one EmailIsOptIn consent configuration found. Please check the 'Consent Configs for this action."
    );
  }
  return emailIsConsentConfigs.length === 1;
};
// SW-200 - Temporary EmailIsOptIn - Find all EmailIsOptIn consent configurations.
export const findEmailIsConsentConfigurations = (props: ActionComponentProps<ActionCustomData>): ConsentConfig[] => {
  return props.custom.consent_configs.filter((config) => config.input.type === EMAIL_IS_OPT_IN_CONSENT_METHOD);
};
// SW-200 - Filters out any EmailIsOptIn consent configurations. NB: Used to control the display of the ConsentInputComponentBlocks.
export const removeEmailIsOptInConsent = (consents: Consent[]): Consent[] => {
  return consents.filter((consent) => consent.consent_method !== "onfieldpresent");
};

// turns 1234567 into "1,234,567"
export function formatBigNumber(n: number): string {
  return n.toLocaleString();
}

export function PetitionActionComponent(props: ActionComponentProps<ActionCustomData>) {
  // SW-200 - Determines if the EmailIsOptIn feature is enabled for this action. Only true IFF there is a SINGLE
  //          Consent Configuration with a name containing the EmailIsOptIn marker.
  const emailIsOptInMode = hasSingleEmailIsConsentConfiguration(props);
  const signature_count = props.custom.petition_signers + props.custom.additional_signatures_count;
  const signature_target = calculateActionsTarget(signature_count);
  const [implicitConsentConfigs, explicitConsentConfigs] = partitionConsentConfigs(props.custom.consent_configs);

  const [name, setName] = React.useState(props.ctx.member.name || "");
  const [email, setEmail] = React.useState(props.ctx.member.email || "");
  const [hideSignature, setHideSignature] = React.useState(false);
  const [postcode, setPostcode] = React.useState(props.ctx.member.postcode || "");
  const [petitionConsents, setPetitionConsents] = React.useState<Consent[]>([]);

  const [submitted, setSubmitted] = React.useState<boolean>(false);
  const [decisionMakers, setDecisionMakers] = React.useState<PublicCampaignTarget[] | undefined>(undefined);
  const [useMemberPrefill, setUseMemberPrefill] = React.useState<boolean>(
    !props.takenAction && props.redacted_prefill !== undefined && props.redacted_prefill.has_email
  );
  const [alreadyConsentedTo, setAlreadyConsentedTo] = React.useState<Consent[]>(
    props.takenAction ? [] : props.consents || []
  );
  const { captureOptIn } = useUserAnalyticsService();

  // Note: "one-click-sign" and "AutoSubmit" are the same thing. the "one click" is
  // considered to be a click on an email link, so really it's zero clicks here ;)
  const [attemptingAutoSubmit, setAttemptingAutoSubmit] = React.useState<boolean>(
    useMemberPrefill && props.custom.enable_one_click_sign
  );
  const [comment, setComment] = React.useState<undefined | string>(undefined);
  const form = React.useRef<HTMLDivElement | null>(null);
  const firstInputField = React.useRef<HTMLInputElement | null>(null);
  const submitButton = React.useRef<HTMLButtonElement | null>(null);

  // run on component mount
  React.useEffect(() => {
    loadCaptcha().then(() => {
      const submitParamPresent =
        window.location.href.indexOf("?submit=true") > 0 || window.location.href.indexOf("&submit=true") > 0;
      // once captcha scripts are loaded, try one-click-sign if one click is enabled AND url contains ?submit=true
      if (props.custom.enable_one_click_sign && useMemberPrefill && submitParamPresent) {
        handleSignPetition();
      } // don't allow one-click-sign if one click is enabled but url DOESN'T contain ?submit=true
      else if (props.custom.enable_one_click_sign && useMemberPrefill && !submitParamPresent) {
        setAttemptingAutoSubmit(false);
      } // don't allow one-click-sign if one click is not ticked AND url does contain ?submit=true
      else if (props.custom.enable_one_click_sign === false && useMemberPrefill && submitParamPresent) {
        setAttemptingAutoSubmit(false);
      } // don't allow one-click-sign if one click is not ticked and url DOESN'T contain ?submit=true
      else if (props.custom.enable_one_click_sign === false && useMemberPrefill && !submitParamPresent) {
        setAttemptingAutoSubmit(false);
      }
    });
  }, []);

  //props.custom.enable_one_click_sign
  async function handleSignPetition() {
    // Causes the button to be disabled, prevents multiple submissions
    setSubmitted(true);

    try {
      // petitionConsents is passed to the plugin backend - it is the list of consents a member saw on this step.
      // it is recorded in the DB and passed to the CRM.
      // allConsents is passed to goToNextStep - it is the list of all consents a member has given, including ones for
      // other plugins.  It is used for consents cookie.
      // update the journey context with entered member data
      const allConsents = updateConsents(alreadyConsentedTo, petitionConsents);

      const context: JourneyContext = mergeContexts(props.ctx, { member: { name, email, postcode } });

      await captchaExecute("petition_submit")
        .then((token) => {
          /// SW-200 - Handle the EmailIsOptIn feature. Mutate the petition_submit request as applicable.
          let processedConsents = [...petitionConsents];
          if (emailIsOptInMode && !context.member.email) {
            // NO EMAIL - Add a special fake email ident.
            //
            // If there is no email, then the supporter has not opted in. We add this special mail address to the
            // request to indicate that the supporter has not opted in and to pass email validation / API constraints.
            context.member.email = NO_EMAIL_PROVIDED_IDENT;
            // If we have no email, then we have no consent. So we should remove the consent from the list.
            processedConsents = removeEmailIsOptInConsent(processedConsents);
          } // END SW-200 block.

          return signPetition.call({
            consents: processedConsents,
            context,
            use_prefill_member_data: useMemberPrefill,
            actionGuid: props.action.guid,
            hideSignature: hideSignature,
            petitionTitle: props.custom.title,
            captchaToken: token,
            variantIdx: props.variant_idx,
            campaignTargetUniqueIds: decisionMakers?.map((dm) => dm.unique_id),
            comment,
          });
        })
        .then(() => {
          props.dispatch.gotoNextStep("completed", context, allConsents);
        })
        .finally(() => {
          const hasContactConsent = petitionConsents.some(
            (consent) => consent.consent_level === "explicit_opt_in" && /^contact-consent.+$/.test(consent.public_id)
          );
          const hasOver18Consent = petitionConsents.some(
            (consent) =>
              consent.consent_level === "explicit_opt_in" && /^age-appropriate-design-over18.+$/.test(consent.public_id)
          );
          if (hasContactConsent && hasOver18Consent) {
            captureOptIn({ actionGuid: props.action.guid });
          }
        });
    } catch (e) {
      setSubmitted(false);
      throw e;
    }
  }

  function jumpToForm() {
    // focus the member details form
    firstInputField.current?.focus();
    // scroll to the member details form
    form.current?.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
  }

  function rejectPrefill(e: React.MouseEvent) {
    e.preventDefault();
    setAlreadyConsentedTo([]);
    setUseMemberPrefill(false);
  }

  // just show a loading spinner for auto-signs
  if (attemptingAutoSubmit) {
    return <MemberLoadingSpinner />;
  }

  const implicitConsentsBlock = () =>
    implicitConsentConfigs ? (
      <ConsentInputComponentBlock
        consentConfigs={implicitConsentConfigs}
        alreadyConsentedTo={alreadyConsentedTo}
        updateConsents={(changedConsents) => {
          setPetitionConsents((existingPCs) => updateConsents(existingPCs, changedConsents));
        }}
        displayTopBorder={true}
        displayBottomBorder={true}
      />
    ) : null;

  const { grey300 } = memberTheme.palette.greys;

  return (
    <ActionComponentWrapper>
      <Stack spacing={3}>
        <Box component="img" src={props.custom.image_url} alt={props.custom.image_alt_text} width="100%" />

        <Stack spacing={1} sx={{ display: { xs: "block", sm: "none" } }}>
          <Typography variant="body2">
            <Box id="petition-sig-count" component="span" fontWeight="bold">
              {formatBigNumber(signature_count)}
            </Box>{" "}
            of{" "}
            <Box component="span" fontWeight="bold">
              {formatBigNumber(signature_target)}
            </Box>{" "}
            signatures
          </Typography>
          <SignatureProgressBar count={signature_count} target={signature_target} />
          <Button fullWidth onClick={jumpToForm}>
            {props.custom.sign_button_label}
          </Button>
        </Stack>

        {!decisionMakers && props.custom.decision_target && (
          <CampaignTargetLookup
            member={props.ctx.member}
            onUpdateMember={() => {}}
            targetProviderConfig={props.custom.decision_target}
            onGetTarget={setDecisionMakers}
          />
        )}

        {decisionMakers && (
          // if there are decisionMakers then title is rendered as (e.g.) "To: Dr Decision Maker"
          <Typography variant="h6">To: {decisionMakers?.map((dm) => dm.name).join(", ")}</Typography>
        )}
        {
          // only display petition action title if show_journey_title is true
          props.show_journey_title ? <Typography variant="h6">{props.custom.title}</Typography> : null
        }
        <Box
          sx={{
            "& > h2": {
              ...memberTheme.typography.subtitle1,
            },
            "& > .card": {
              backgroundColor: memberTheme.palette.greys.grey100,
              borderRadius: "12px",
              borderColour: memberTheme.palette.greys.grey300,
              padding: "20px",
            },
          }}
          dangerouslySetInnerHTML={{ __html: props.custom.page_content }}
        />

        {useMediaQuery(memberTheme.breakpoints.up("md")) && implicitConsentsBlock()}
      </Stack>

      {/*                  */}
      {/* PETITION FORM UI */}
      {/*                  */}
      <Container
        disableGutters={useMediaQuery(memberTheme.breakpoints.down("md"))}
        sx={{
          [memberTheme.breakpoints.up("md")]: {
            border: "1px",
            borderColor: grey300,
            borderRadius: "8px",
            boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.25)",
            padding: "24px",
          },
        }}
      >
        <SignatureProgressBar count={signature_count} target={signature_target} />

        <Stack
          spacing={{ xs: 0, md: 3 }}
          direction={{ xs: "row", md: "column" }}
          marginTop="0.5rem"
          justifyContent={{ xs: "normal", md: "space-between" }}
        >
          <Box minWidth={{ xs: "50%" }}>
            <Typography variant="h4" color="primary">
              {formatBigNumber(signature_count)}
            </Typography>
            <Typography variant="body2">of {formatBigNumber(signature_target)} signatures</Typography>
          </Box>
          <RecentSignatures
            petitionGuid={props.action.guid}
            petitionRecentSignatureLabel={props.custom.recent_signature_label}
          />
        </Stack>

        <Divider />

        {/* used for scrollIntoView */}
        <span ref={form} />

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSignPetition();
          }}
        >
          <FormControl fullWidth>
            {props.redacted_prefill && useMemberPrefill ? (
              <Box>
                You&apos;re signing this as
                <Typography>
                  <strong className="posthog-mask-text">{props.redacted_prefill.name}</strong>
                </Typography>
                <Box>
                  Not you?
                  <Button
                    onClick={rejectPrefill}
                    sx={{
                      backgroundColor: "#FFFFFF",
                      color: memberTheme.palette.blue.main,
                      "&:hover": {
                        backgroundColor: "transparent",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Click here
                  </Button>
                </Box>
              </Box>
            ) : (
              <>
                {props.isStaff && props.takenAction && (
                  <Paper variant="outlined" sx={{ p: 2, marginBottom: "1rem" }}>
                    <Typography>38 Degrees Staff:</Typography>
                    <Divider />
                    <Typography>You have already taken this action, so we have not prefilled your details</Typography>
                  </Paper>
                )}
                <RequiredTextArea
                  id="member-name"
                  value={name}
                  label="Name"
                  onChange={(e: React.ChangeEvent<HTMLInputElement> | string) => {
                    setName(typeof e === "string" ? e : e.target.value);
                  }}
                  ref={firstInputField}
                />
                {/* SW-200 - Added isEmailOptional */}
                <EmailInput value={email} onChange={setEmail} isEmailOptional={emailIsOptInMode} />
                <PostcodeInput value={postcode} onChange={setPostcode} />

                <FormGroup>
                  <FormControlLabel
                    sx={{ marginBottom: "0px" }}
                    control={
                      <Checkbox
                        checked={hideSignature}
                        onChange={({ target: { checked } }) => setHideSignature(checked)}
                      />
                    }
                    label={<Typography variant="caption">Don&apos;t display name publicly</Typography>}
                  />
                </FormGroup>
              </>
            )}

            <Divider />

            {props.custom.comments_enabled && (
              <>
                <Box sx={{ marginTop: "0.25rem" }}>
                  <TextField
                    id="petition-comment"
                    value={comment}
                    label="Your comments"
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Why is this important to you?"
                    multiline
                    fullWidth
                    minRows={3}
                    maxRows={6}
                  />
                </Box>
                <Divider />
              </>
            )}

            <ConsentInputComponentBlock
              consentConfigs={explicitConsentConfigs}
              alreadyConsentedTo={alreadyConsentedTo}
              updateConsents={(changedConsents) => {
                setPetitionConsents((existingPCs) => updateConsents(existingPCs, changedConsents));
              }}
              displayBottomBorder={true}
            />

            <Button ref={submitButton} type="submit" fullWidth disabled={submitted}>
              {props.custom.sign_button_label}
            </Button>

            {useMediaQuery(memberTheme.breakpoints.down("md")) && implicitConsentsBlock()}
          </FormControl>
        </form>
      </Container>
    </ActionComponentWrapper>
  );
}

function SignatureProgressBar(props: { count: number; target: number }) {
  const targetPercent = (100 * props.count) / props.target;

  return (
    <LinearProgress
      variant="determinate"
      value={targetPercent}
      sx={{
        width: "100%",
        height: "8px",
        borderRadius: "0",
      }}
      aria-label="Petition signatures"
      aria-valuenow={props.count}
      aria-valuemin={0}
      aria-valuemax={props.target}
    />
  );
}
